import { useState, useEffect } from "react";
import { useLocation } from "wouter";

/**
 * 注册页 — Phase 2.5 方案 A
 *
 * 功能:
 *  - 邀请码必填 (URL 参数 ?invite=XXX 自动填)
 *  - 手机/邮箱 + SMS 验证码 + 密码 (8 位以上)
 *  - 注册成功自动登录跳转主页
 *  - 设备指纹 (UA + screen + tz)
 */

const DEFAULT_INVITE_CODE = "";

// Cloudflare Turnstile site key（VITE_TURNSTILE_SITE_KEY 未配则不渲染 widget，
// 后端 verifyTurnstile 也会因 TURNSTILE_SECRET 未配跳过）
const TURNSTILE_SITE_KEY = (import.meta as any).env?.VITE_TURNSTILE_SITE_KEY || "";

function getDeviceFingerprint(): string {
  try {
    const parts = [
      navigator.userAgent.slice(0, 80),
      `${screen.width}x${screen.height}`,
      Intl.DateTimeFormat().resolvedOptions().timeZone,
      navigator.language,
    ];
    return parts.join("|").slice(0, 128);
  } catch {
    return "unknown";
  }
}

export default function Register() {
  const [, setLocation] = useLocation();
  const [account, setAccount] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [inviteCode, setInviteCode] = useState(DEFAULT_INVITE_CODE);
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [loading, setLoading] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [showPwd, setShowPwd] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState("");

  // URL 参数 ?invite=XXX 自动填
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const inv = params.get("invite") || params.get("inviteCode") || "";
    if (inv) setInviteCode(inv.trim());
  }, []);

  useEffect(() => {
    if (countdown <= 0) return;
    const t = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown]);

  // Cloudflare Turnstile widget 加载（site key 未配则跳过）
  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return;
    const renderTurnstile = () => {
      const w = window as any;
      if (!w.turnstile) return;
      const el = document.getElementById("turnstile-container");
      if (!el || el.dataset.rendered === "1") return;
      el.dataset.rendered = "1";
      w.turnstile.render(el, {
        sitekey: TURNSTILE_SITE_KEY,
        callback: (t: string) => setTurnstileToken(t),
        "error-callback": () => setTurnstileToken(""),
        "expired-callback": () => setTurnstileToken(""),
        theme: "dark",
      });
    };
    if ((window as any).turnstile) {
      renderTurnstile();
      return;
    }
    const existing = document.querySelector('script[data-turnstile]');
    if (existing) {
      existing.addEventListener("load", renderTurnstile);
      return;
    }
    const s = document.createElement("script");
    s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    s.async = true;
    s.defer = true;
    s.dataset.turnstile = "1";
    s.onload = renderTurnstile;
    document.body.appendChild(s);
  }, []);

  const sendCode = async () => {
    setError(""); setInfo("");
    if (!account.trim()) { setError("请输入手机号或邮箱"); return; }
    setLoading(true);
    try {
      const res = await fetch("/api/auth/send-code", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ identifier: account.trim() }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || "发送失败"); return; }
      setCodeSent(true); setCountdown(60);
      setInfo(data.message || "验证码已发送");
    } catch { setError("网络错误，请重试"); }
    finally { setLoading(false); }
  };

  const handleRegister = async () => {
    setError(""); setInfo("");
    if (!account.trim()) { setError("请输入手机号或邮箱"); return; }
    if (!password || password.length < 8) { setError("密码至少 8 位"); return; }
    if (!code.trim()) { setError("请先获取并输入验证码"); return; }
    if (TURNSTILE_SITE_KEY && !turnstileToken) { setError("请完成人机验证"); return; }

    setLoading(true);
    try {
      const res = await fetch("/api/auth/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          identifier: account.trim(),
          password,
          code: code.trim(),
          inviteCode: inviteCode.trim(),
          name: name.trim() || undefined,
          deviceFingerprint: getDeviceFingerprint(),
          turnstileToken: turnstileToken || undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || "注册失败"); return; }
      setInfo(`注册成功! 已开通 ${data.user?.planCode || ""} 套餐, 试用 ${data.user?.trialDays || 7} 天`);
      setTimeout(() => { window.location.href = "/"; }, 1500);
    } catch { setError("网络错误，请重试"); }
    finally { setLoading(false); }
  };

  const onKey = (e: React.KeyboardEvent) => { if (e.key === "Enter") handleRegister(); };

  return (
    <>
      <style>{`
        .reg-root *{box-sizing:border-box;margin:0;padding:0;}
        .reg-root{min-height:100vh;min-height:100dvh;display:flex;align-items:center;justify-content:center;
          background:linear-gradient(135deg,#0f172a 0%,#1e2d4a 50%,#0f172a 100%);
          font-family:-apple-system,BlinkMacSystemFont,'PingFang SC','Microsoft YaHei','Helvetica Neue',sans-serif;
          padding:16px;position:relative;overflow:hidden;}
        .reg-root::before{content:'';position:absolute;inset:0;
          background-image:linear-gradient(rgba(99,155,255,.05) 1px,transparent 1px),linear-gradient(90deg,rgba(99,155,255,.05) 1px,transparent 1px);
          background-size:40px 40px;pointer-events:none;}
        .rg-glow1{position:absolute;width:500px;height:500px;border-radius:50%;
          background:radial-gradient(circle,rgba(245,158,11,.13) 0%,transparent 70%);top:-150px;left:-150px;pointer-events:none;}
        .rg-glow2{position:absolute;width:400px;height:400px;border-radius:50%;
          background:radial-gradient(circle,rgba(139,92,246,.10) 0%,transparent 70%);bottom:-100px;right:-100px;pointer-events:none;}
        .rg-card{position:relative;width:100%;max-width:440px;
          background:rgba(30,45,74,.95);backdrop-filter:blur(20px);-webkit-backdrop-filter:blur(20px);
          border:1px solid rgba(255,255,255,.10);border-radius:20px;padding:28px 32px;
          box-shadow:0 0 0 1px rgba(245,158,11,.08),0 24px 64px rgba(0,0,0,.55),0 0 60px rgba(245,158,11,.06);}
        .rg-card::before{content:'';position:absolute;top:0;left:20%;right:20%;height:1px;
          background:linear-gradient(90deg,transparent,rgba(245,158,11,.6),transparent);pointer-events:none;}
        .rg-logo{display:flex;align-items:center;gap:10px;margin-bottom:18px;}
        .rg-logo-icon{width:38px;height:38px;border-radius:11px;
          background:linear-gradient(135deg,#f59e0b,#dc2626);
          display:flex;align-items:center;justify-content:center;font-size:20px;
          box-shadow:0 4px 14px rgba(245,158,11,.40);flex-shrink:0;}
        .rg-logo-name{color:#f1f5f9;font-size:16px;font-weight:700;line-height:1.2;letter-spacing:.2px;}
        .rg-logo-sub{color:#64748b;font-size:10.5px;text-transform:uppercase;letter-spacing:1.2px;margin-top:1px;}
        .rg-h{color:#f8fafc;font-size:22px;font-weight:700;letter-spacing:-.3px;margin-bottom:4px;}
        .rg-sub{color:#7a90a8;font-size:13px;margin-bottom:18px;}
        .rg-field{margin-bottom:12px;}
        .rg-label{display:block;color:#94a3b8;font-size:12px;font-weight:500;margin-bottom:7px;letter-spacing:.3px;}
        .rg-label .req{color:#fb923c;margin-left:3px;}
        .rg-iw{position:relative;display:flex;gap:8px;}
        .rg-input{width:100%;background:rgba(255,255,255,.07);border:1px solid rgba(255,255,255,.12);
          border-radius:10px;padding:10px 14px;color:#f1f5f9;font-size:15px;font-family:inherit;
          outline:none;transition:border-color .2s,box-shadow .2s;-webkit-appearance:none;}
        .rg-input::placeholder{color:#4a5f7a;}
        .rg-input:focus{border-color:rgba(245,158,11,.55);box-shadow:0 0 0 3px rgba(245,158,11,.10);}
        .rg-input.pr{padding-right:46px;}
        .rg-input.code{letter-spacing:6px;font-size:20px;font-weight:600;text-align:center;color:#fbbf24;}
        .rg-input.invite{font-family:'JetBrains Mono','Courier New',monospace;font-size:14px;letter-spacing:2px;color:#fbbf24;}
        .rg-input.invite::placeholder{letter-spacing:0;color:#4a5f7a;font-family:inherit;}
        .rg-eye{position:absolute;right:12px;top:50%;transform:translateY(-50%);
          background:none;border:none;cursor:pointer;color:#64748b;
          padding:4px;display:flex;align-items:center;transition:color .2s;}
        .rg-eye:hover{color:#94a3b8;}
        .rg-send-btn{flex-shrink:0;white-space:nowrap;background:rgba(245,158,11,.18);
          border:1px solid rgba(251,191,36,.35);border-radius:10px;
          padding:0 16px;color:#fbbf24;font-size:13px;font-weight:500;
          cursor:pointer;font-family:inherit;transition:all .2s;}
        .rg-send-btn:hover:not(:disabled){background:rgba(245,158,11,.30);}
        .rg-send-btn:disabled{opacity:.5;cursor:not-allowed;}
        .rg-hint{color:#4a6282;font-size:11px;margin-top:5px;line-height:1.5;}
        .rg-err{display:flex;align-items:center;gap:8px;
          background:rgba(239,68,68,.09);border:1px solid rgba(239,68,68,.22);
          border-radius:8px;padding:9px 12px;color:#fca5a5;font-size:13px;margin-bottom:14px;}
        .rg-info{display:flex;align-items:center;gap:8px;
          background:rgba(34,197,94,.09);border:1px solid rgba(34,197,94,.22);
          border-radius:8px;padding:9px 12px;color:#86efac;font-size:13px;margin-bottom:14px;}
        .rg-btn{width:100%;background:linear-gradient(135deg,#f59e0b 0%,#dc2626 100%);
          border:none;border-radius:10px;padding:13px 0;color:#fff;
          font-size:15px;font-weight:600;font-family:inherit;cursor:pointer;
          transition:opacity .2s,transform .1s,box-shadow .2s;
          box-shadow:0 4px 20px rgba(245,158,11,.32);letter-spacing:.3px;
          position:relative;overflow:hidden;}
        .rg-btn::after{content:'';position:absolute;inset:0;background:linear-gradient(rgba(255,255,255,.08),transparent);}
        .rg-btn:hover:not(:disabled){opacity:.9;box-shadow:0 6px 26px rgba(245,158,11,.42);}
        .rg-btn:active:not(:disabled){transform:scale(.99);}
        .rg-btn:disabled{opacity:.55;cursor:not-allowed;}
        .rg-note{text-align:center;color:#4a5f7a;font-size:12px;margin-top:14px;line-height:1.8;}
        .rg-link{color:#fbbf24;text-decoration:none;cursor:pointer;font-size:12px;}
        .rg-link:hover{text-decoration:underline;}
        .rg-invite-pill{display:inline-flex;align-items:center;gap:6px;
          background:rgba(245,158,11,.12);border:1px solid rgba(251,191,36,.30);
          border-radius:6px;padding:3px 8px;color:#fbbf24;font-size:11px;font-weight:500;
          margin-top:5px;}
        @keyframes spin{to{transform:rotate(360deg);}}
        .rg-spin{display:inline-block;width:13px;height:13px;
          border:2px solid rgba(255,255,255,.25);border-top-color:#fff;
          border-radius:50%;animation:spin .7s linear infinite;
          vertical-align:middle;margin-right:6px;}
        @media(max-width:480px){.rg-card{padding:20px 16px;border-radius:16px;}.rg-h{font-size:20px;}}
      `}</style>

      <div className="reg-root">
        <div className="rg-glow1"/><div className="rg-glow2"/>
        <div className="rg-card">

          <div className="rg-logo">
            <div className="rg-logo-icon">🎟️</div>
            <div>
              <div className="rg-logo-name">AI选股与量化交易系统</div>
              <div className="rg-logo-sub">Quant Trading Platform</div>
            </div>
          </div>

          <h2 className="rg-h">创建账号</h2>
          <p className="rg-sub">注册即送积分 · 7 天免费试用 · 邀请码可选 (有则按套餐升级)</p>

          {/* 邀请码 (必填) */}
          <div className="rg-field">
            <label className="rg-label">邀请码 <span style={{color:"#64748b",fontWeight:400}}>(可选)</span></label>
            <input className="rg-input invite" type="text" maxLength={32}
              placeholder="请输入邀请码" value={inviteCode}
              onChange={e => setInviteCode(e.target.value.trim().toUpperCase())} onKeyDown={onKey}/>
            <p className="rg-hint">有邀请码会按对应套餐开通; 无邀请码默认免费套餐 + 7 天试用</p>
          </div>

          {/* 账号 */}
          <div className="rg-field">
            <label className="rg-label">手机号 / 邮箱<span className="req">*</span></label>
            <input className="rg-input" type="text" inputMode="email" autoComplete="username"
              placeholder="请输入手机号或邮箱" value={account}
              onChange={e => setAccount(e.target.value)} onKeyDown={onKey}/>
          </div>

          {/* 昵称 (可选) */}
          <div className="rg-field">
            <label className="rg-label">昵称 <span style={{color:"#64748b",fontWeight:400}}>(可选)</span></label>
            <input className="rg-input" type="text" maxLength={32}
              placeholder="请输入昵称" value={name}
              onChange={e => setName(e.target.value)} onKeyDown={onKey}/>
          </div>

          {/* 密码 */}
          <div className="rg-field">
            <label className="rg-label">设置密码<span className="req">*</span> <span style={{color:"#64748b",fontWeight:400}}>(至少 8 位)</span></label>
            <div style={{position:"relative"}}>
              <input className="rg-input pr" type={showPwd ? "text" : "password"}
                autoComplete="new-password"
                placeholder="至少 8 位, 建议含字母与数字" value={password}
                onChange={e => setPassword(e.target.value)} onKeyDown={onKey}/>
              <button className="rg-eye" type="button" onClick={() => setShowPwd(v => !v)} tabIndex={-1}>
                {showPwd ? (
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94"/>
                    <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19"/>
                    <line x1="1" y1="1" x2="23" y2="23"/>
                  </svg>
                ) : (
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                )}
              </button>
            </div>
          </div>

          {/* SMS 验证码 */}
          <div className="rg-field">
            <label className="rg-label">短信/邮箱验证码<span className="req">*</span></label>
            <div className="rg-iw">
              <input className="rg-input code" type="text" inputMode="numeric"
                maxLength={6} placeholder="──────"
                value={code} onChange={e => setCode(e.target.value.replace(/\D/g,""))} onKeyDown={onKey}/>
              <button className="rg-send-btn" onClick={sendCode}
                disabled={loading || countdown > 0 || !account.trim()}>
                {countdown > 0 ? `${countdown}s` : codeSent ? "重新获取" : "获取验证码"}
              </button>
            </div>
            <p className="rg-hint">验证码 10 分钟内有效</p>
          </div>

          {TURNSTILE_SITE_KEY && (
            <div className="rg-field" style={{display:"flex",justifyContent:"center"}}>
              <div id="turnstile-container" />
            </div>
          )}

          {info && <div className="rg-info"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>{info}</div>}
          {error && <div className="rg-err"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>{error}</div>}

          <button className="rg-btn" onClick={handleRegister} disabled={loading}>
            {loading && <span className="rg-spin"/>}
            {loading ? "处理中..." : "🎟️ 注册账号"}
          </button>

          <div className="rg-note">
            已有账号? <a className="rg-link" onClick={() => setLocation("/login")}>直接登录</a>
            <br/>
            <span style={{color:"#4a5f7a",fontSize:11,marginTop:6,display:"inline-block"}}>
              注册即表示同意服务条款 · 提交即视为知悉隐私政策
            </span>
          </div>
        </div>
      </div>
    </>
  );
}
